import { useMemo, useState, type ReactNode } from 'react';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid2';
import Alert from '@mui/material/Alert';
import { PageSection, StatTile } from '@radiology/ui';
import { DocumentUpload, TickCircle, CloseCircle, Danger } from 'iconsax-react';
import Button from '@mui/material/Button';
import { CertificateTable } from './CertificateTable';
import { RequirementTable, type RequirementFilter } from './RequirementTable';
import { ReviewDialog } from './ReviewDialog';
import { UploadDialog } from './UploadDialog';
import { HistoryDialog } from './HistoryDialog';
import {
  useCertificates,
  useMyExtraCertificates,
  useRequirementTotals,
  useStatusSummary,
  useWithdrawCertificate,
  type Certificate,
  type Requirement,
} from './api';
import { useSession } from '../../auth/SessionContext';
import { PageSection as Section } from '@radiology/ui';
import { useConfirm, useToast } from '@radiology/ui';

/**
 * The employee's own actions, shared by the dashboard and My certificates.
 *
 * All four of them — upload, renew, replace and withdraw — plus the dialogs
 * they open, because both screens offer the same actions and having them drift
 * apart is exactly what happened in the legacy app.
 */
function useOwnerActions() {
  const [open, setOpen] = useState(false);
  const [replacing, setReplacing] = useState<Certificate | null>(null);
  const [renewing, setRenewing] = useState<string | null>(null);
  const [history, setHistory] = useState<string | null>(null);
  const { data: certificates } = useCertificates();
  const withdraw = useWithdrawCertificate();
  const confirm = useConfirm();
  const toast = useToast();

  const close = () => {
    setOpen(false);
    setReplacing(null);
    setRenewing(null);
  };

  /**
   * CERT-02 / CERT-03 — renew one requirement.
   *
   * Takes the certificate TYPE, not a certificate row, and that is the whole
   * point: the renewal is a new version of the requirement, so it must not
   * carry a reference to the document it supersedes.
   */
  const onRenew = (requirement: Requirement) => {
    setReplacing(null);
    setRenewing(requirement.certificate_type);
    setOpen(true);
  };

  /** Re-submit a rejected attempt. Identified by id — the row IS overwritten. */
  const onReplaceById = (certificateId: string) => {
    const certificate = (certificates ?? []).find(c => c.id === certificateId);
    if (!certificate) {
      toast.error('That certificate could not be found. Refresh and try again.');
      return;
    }
    setRenewing(null);
    setReplacing(certificate);
    setOpen(true);
  };

  const onReplace = (certificate: Certificate) => {
    setRenewing(null);
    setReplacing(certificate);
    setOpen(true);
  };

  const onWithdraw = async (certificate: Certificate) => {
    const ok = await confirm({
      title: `Withdraw ${certificate.name}?`,
      message:
        'It is removed along with the file you uploaded. You can upload it again afterwards. Only a certificate nobody has reviewed yet can be withdrawn.',
      confirmLabel: 'Withdraw',
      tone: 'danger',
    });
    if (!ok) return;

    withdraw.mutate(
      { id: certificate.id, path: certificate.file_name },
      {
        onSuccess: () => toast.success('Certificate withdrawn'),
        onError: (e: Error) => toast.error(e.message),
      },
    );
  };

  const uploadButton = (
    <Button
      variant="contained"
      onClick={() => {
        setReplacing(null);
        setRenewing(null);
        setOpen(true);
      }}
    >
      Upload a certificate
    </Button>
  );

  const dialogs = (
    <>
      <UploadDialog open={open} replacing={replacing} renewing={renewing} onClose={close} />
      <HistoryDialog certificateType={history} onClose={() => setHistory(null)} />
    </>
  );

  return {
    owner: { onReplace, onWithdraw },
    requirementActions: { onRenew, onReplace: onReplaceById, onHistory: setHistory },
    uploadButton,
    dialogs,
  };
}

/**
 * Certifications screens.
 *
 * Every one of these renders the same components against the same queries — the
 * difference between what an employee and a manager see is enforced by RLS, not
 * by these files. That is why there is no hospital or section filtering here.
 */

function useTotals() {
  const { data } = useStatusSummary();
  return useMemo(() => {
    const rows = data ?? [];
    const sum = (s: string) =>
      rows.filter(r => r.status === s).reduce((n, r) => n + Number(r.total), 0);
    return {
      valid: sum('valid'),
      pending: sum('pending'),
      expiringSoon: sum('expiring soon'),
      expired: sum('expired'),
      rejected: sum('rejected'),
    };
  }, [data]);
}

/**
 * Employee — their own compliance position, by requirement.
 *
 * CERT-01 makes the four cards a filter rather than a read-out: clicking one
 * drills into the certificate types behind the number. Clicking the selected
 * card again clears it, which is the behaviour people expect from a toggle and
 * saves hunting for a "show all" control.
 */
export function EmployeeDashboard() {
  const { totals } = useRequirementTotals();
  const { requirementActions, uploadButton, dialogs } = useOwnerActions();
  const [filter, setFilter] = useState<RequirementFilter>(null);

  const toggle = (next: RequirementFilter) =>
    setFilter(current => (current === next ? null : next));

  const CARDS: {
    key: Exclude<RequirementFilter, null>;
    label: string;
    value: number;
    tone: 'success' | 'warning' | 'error' | 'info';
    hint?: string;
    icon: ReactNode;
  }[] = [
    {
      key: 'Valid',
      label: 'Valid',
      value: totals.valid,
      tone: 'success',
      icon: <TickCircle size={20} color="currentColor" />,
    },
    {
      key: 'Expiring Soon',
      label: 'Expiring soon',
      value: totals.expiringSoon,
      tone: 'warning',
      hint: 'Within 45 days',
      icon: <Danger size={20} color="currentColor" />,
    },
    {
      key: 'Expired',
      label: 'Expired',
      value: totals.expired,
      tone: 'error',
      icon: <CloseCircle size={20} color="currentColor" />,
    },
    {
      key: 'awaiting',
      label: 'Awaiting review',
      value: totals.awaitingReview,
      tone: 'info',
      // Says out loud why this number can overlap the others (CERT-06).
      hint: 'Not yet approved',
      icon: <DocumentUpload size={20} color="currentColor" />,
    },
  ];

  return (
    <Stack gap={2.5}>
      <Grid container spacing={2.5}>
        {CARDS.map(card => (
          <Grid key={card.key} size={{ xs: 12, sm: 6, lg: 3 }}>
            <StatTile
              label={card.label}
              value={card.value}
              tone={card.tone}
              hint={card.hint}
              icon={card.icon}
              onClick={() => toggle(card.key)}
              selected={filter === card.key}
            />
          </Grid>
        ))}
      </Grid>

      {/* Required, with no approved certificate behind it — never uploaded, still
          in review, or rejected. Its own callout rather than being folded into
          Expired: the CR defines four statuses and does not say which one covers
          this, and quietly adding it to Expired would change a number the
          business is going to check. */}
      {totals.missing > 0 && (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={() => toggle('Missing')}>
              {filter === 'Missing' ? 'Show all' : 'Show'}
            </Button>
          }
        >
          {totals.missing} required {totals.missing === 1 ? 'certificate has' : 'certificates have'}{' '}
          no approved certificate yet.
        </Alert>
      )}

      {/* "Renew it" only when there is in fact something to renew. An expired
          requirement whose renewal is already with the supervisor is still
          Expired (CERT-06) and still counted on the card above, but telling
          that person to renew it sends them looking for a button that CERT-02
          deliberately does not show them. */}
      {totals.expired > 0 && (
        <Alert severity="error">
          {totals.expired} of your required{' '}
          {totals.expired === 1 ? 'certificates is' : 'certificates are'} expired.{' '}
          {totals.expiredActionable > 0
            ? `Renew ${totals.expiredActionable === 1 ? 'it' : 'them'} to stay compliant.`
            : 'Your renewal is with your supervisor — it counts once they approve it.'}
        </Alert>
      )}

      <Stack direction="row" justifyContent="flex-end">
        {uploadButton}
      </Stack>

      {/* Unfiltered, this is "Needs attention" — the CR's impacted-screens table
          keeps that section on the Employee Dashboard, and a dashboard listing
          every requirement would just be My certificates twice. Selecting a
          card replaces it with that card's drill-down, including Valid, which
          is what CERT-01 asks for. */}
      <PageSection
        title={filter ? `${FILTER_LABELS[filter]} certificates` : 'Needs attention'}
        description={
          filter
            ? 'Click the card again to go back to what needs attention.'
            : 'Expired, expiring within 45 days, and anything sent back to you. Click a card above to see the rest.'
        }
        disablePadding
      >
        <RequirementTable
          filter={filter}
          needsAttentionOnly={!filter}
          {...requirementActions}
          emptyTitle={
            filter ? `Nothing ${FILTER_LABELS[filter].toLowerCase()}` : 'Nothing needs attention'
          }
          emptyDescription={
            filter
              ? undefined
              : 'Every certificate you are required to hold is valid, and none expires in the next 45 days.'
          }
        />
      </PageSection>

      {dialogs}
    </Stack>
  );
}

/** Heading text for a drill-down. */
const FILTER_LABELS: Record<Exclude<RequirementFilter, null>, string> = {
  Valid: 'Valid',
  'Expiring Soon': 'Expiring soon',
  Expired: 'Expired',
  Missing: 'Missing',
  awaiting: 'Awaiting review',
};

export function MyCertificates() {
  const { owner, requirementActions, uploadButton, dialogs } = useOwnerActions();
  const extras = useMyExtraCertificates();

  return (
    <Stack gap={2.5}>
      <Stack direction="row" justifyContent="flex-end">
        {uploadButton}
      </Stack>

      <Section
        title="My certificates"
        description="One row per certificate you are required to hold. Every version you have ever uploaded is kept — open the history to see them."
        disablePadding
      >
        <RequirementTable
          {...requirementActions}
          emptyTitle="Nothing required"
          emptyDescription="No certificates are configured for your title yet. Ask your administrator."
        />
      </Section>

      {/* Certificates that are not on any list that applies to this person —
          usually because their Title changed, or because the type was retired.
          They stopped counting towards the cards above when requirements
          replaced files (CERT-05); they have not stopped existing, and the CR
          is explicit that a Title change must not delete history. */}
      {(extras.data?.length ?? 0) > 0 && (
        <Section
          title="Other certificates on record"
          description="Not required for your current title, so these do not affect the counts above. They are kept on your record."
          disablePadding
        >
          <CertificateTable
            showOwner={false}
            owner={owner}
            onlyIds={new Set((extras.data ?? []).map(e => e.id))}
            emptyTitle="Nothing else on record"
          />
        </Section>
      )}

      {dialogs}
    </Stack>
  );
}

/** Supervisor — the review queues. */
function ReviewQueue({
  status,
  title,
  description,
  emptyTitle,
  reviewable,
}: {
  status: 'pending' | 'valid' | 'rejected';
  title: string;
  description: string;
  emptyTitle: string;
  reviewable?: boolean;
}) {
  const [selected, setSelected] = useState<Certificate | null>(null);
  const session = useSession();

  return (
    <>
      <PageSection title={title} description={description} disablePadding>
        <CertificateTable
          status={status}
          emptyTitle={emptyTitle}
          onReview={reviewable ? setSelected : undefined}
        />
      </PageSection>
      <ReviewDialog
        certificate={selected}
        reviewerName={session.user.displayName ?? session.user.username}
        onClose={() => setSelected(null)}
      />
    </>
  );
}

export const SupervisorPending = () => (
  <ReviewQueue
    status="pending"
    title="Pending review"
    description="Certificates in your section waiting for a decision."
    emptyTitle="Nothing waiting"
    reviewable
  />
);

export const SupervisorApproved = () => (
  <ReviewQueue
    status="valid"
    title="Approved"
    description="Certificates you have approved that are still valid."
    emptyTitle="Nothing approved yet"
  />
);

export const SupervisorRejected = () => (
  <ReviewQueue
    status="rejected"
    title="Rejected"
    description="Certificates sent back. The person can see the comment and re-upload."
    emptyTitle="Nothing rejected"
  />
);

/** Manager — hospital-wide, read-only. */
export function ManagerDashboard() {
  const t = useTotals();
  const { data } = useStatusSummary();

  const bySection = useMemo(() => {
    const map = new Map<string, { section: string; total: number; overdue: number }>();
    for (const row of data ?? []) {
      const entry = map.get(row.section) ?? { section: row.section, total: 0, overdue: 0 };
      entry.total += Number(row.total);
      entry.overdue += Number(row.overdue);
      map.set(row.section, entry);
    }
    return [...map.values()].sort((a, b) => b.overdue - a.overdue);
  }, [data]);

  return (
    <Stack gap={2.5}>
      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile label="Valid" value={t.valid} tone="success" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            label="Expiring soon"
            value={t.expiringSoon}
            tone="warning"
            hint="Within 45 days"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile label="Expired" value={t.expired} tone="error" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile label="Awaiting review" value={t.pending} tone="info" />
        </Grid>
      </Grid>

      <PageSection
        title="By section"
        description="Sections with the most expired certificates first."
      >
        <Stack gap={1}>
          {bySection.map(s => (
            <Stack
              key={s.section}
              direction="row"
              justifyContent="space-between"
              sx={{ py: 1, borderBottom: t => `1px solid ${t.palette.divider}` }}
            >
              <span>{s.section}</span>
              <span>
                {s.overdue > 0 && (
                  <strong style={{ color: '#D64545', marginInlineEnd: 12 }}>
                    {s.overdue} expired
                  </strong>
                )}
                {s.total} total
              </span>
            </Stack>
          ))}
        </Stack>
      </PageSection>
    </Stack>
  );
}

export const CertificateReports = () => (
  <PageSection
    title="Certificate reports"
    description="Every certificate in your hospital."
    disablePadding
  >
    <CertificateTable emptyTitle="No certificates" />
  </PageSection>
);

/**
 * Admin — unscoped.
 *
 * RLS gives an admin every certificate in every hospital, so this is the same
 * component as everyone else's, just without a scope narrowing it. The legacy
 * admin nav had no certificate view at all, which meant admins could not see the
 * data they administer.
 */
export function AdminAllCertificates() {
  return (
    <PageSection
      title="All certificates"
      description="Every certificate across all hospitals and sections."
      disablePadding
    >
      <CertificateTable emptyTitle="No certificates" />
    </PageSection>
  );
}

export function AdminOverview() {
  const t = useTotals();
  const { data } = useStatusSummary();

  const byHospital = useMemo(() => {
    const map = new Map<
      string,
      { hospital: string; total: number; overdue: number; pending: number }
    >();
    for (const row of data ?? []) {
      const e = map.get(row.hospital) ?? {
        hospital: row.hospital,
        total: 0,
        overdue: 0,
        pending: 0,
      };
      e.total += Number(row.total);
      e.overdue += Number(row.overdue);
      if (row.status === 'pending') e.pending += Number(row.total);
      map.set(row.hospital, e);
    }
    return [...map.values()].sort((a, b) => b.total - a.total);
  }, [data]);

  return (
    <Stack gap={2.5}>
      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile label="Valid" value={t.valid} tone="success" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            label="Expiring soon"
            value={t.expiringSoon}
            tone="warning"
            hint="Within 45 days"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile label="Expired" value={t.expired} tone="error" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile label="Awaiting review" value={t.pending} tone="info" />
        </Grid>
      </Grid>

      <PageSection title="By hospital" description="Every hospital, most certificates first.">
        <Stack gap={1}>
          {byHospital.map(h => (
            <Stack
              key={h.hospital}
              direction="row"
              justifyContent="space-between"
              sx={{ py: 1, borderBottom: t => `1px solid ${t.palette.divider}` }}
            >
              <span>{h.hospital}</span>
              <span>
                {h.pending > 0 && <span style={{ marginInlineEnd: 12 }}>{h.pending} pending</span>}
                {h.overdue > 0 && (
                  <strong style={{ color: '#D64545', marginInlineEnd: 12 }}>
                    {h.overdue} expired
                  </strong>
                )}
                {h.total} total
              </span>
            </Stack>
          ))}
        </Stack>
      </PageSection>
    </Stack>
  );
}
